import { organizationInitializedGuard } from './core/guards/organization-initialized.guard';
import { EstablishmentsComponent } from './core/pages/establishments/establishments.component';
import { establishmentBreadcrumbResolver } from './core/resolvers/breadcrumbs/establishment-breadcrumb.resolver';
import { HomePageComponent } from './core/pages/home-page/home-page.component';
import { OrganizationNotFoundComponent } from './core/pages/organizations/not-found/organization-not-found.component';
import { organizationNotInitializedGuard } from './core/guards/organization-not-initialized.guard';
import { AppRoute } from './app-route';
import { redirectAwayGuard } from './core/guards/homepage/redirect-away.guard';
import { NotFoundComponent } from './http/not-found/not-found.component';
import { authWithPortalRedirectGuard } from './core/guards/auth-with-portal-redirect.guard';
import { ProfilePageComponent } from './core/pages/profile/profile-page.component';
import { rootDomainGuard } from './core/guards/root-domain.guard';
import { superadminGuard } from './core/guards/superadmin.guard';
import { agentGuard } from './core/guards/agent.guard';
import { managementGuard } from './core/guards/management.guard';

export const routes: AppRoute[] = [
    // Root domain landing page (ares.sale only)
    {
        path: '',
        loadComponent: () => import('./core/pages/landing/landing-page.component').then(m => m.LandingPageComponent),
        title: 'ARES Salesplatform',
        canMatch: [rootDomainGuard],
    },
    // Redirect old portal URL to login
    {
        path: 'portal',
        redirectTo: '/login',
        pathMatch: 'full'
    },
    // Legal pages (accessible from anywhere)
    {
        path: 'privacy',
        loadComponent: () => import('./core/pages/legal/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
        title: 'Privacy Policy - ARES',
    },
    {
        path: 'terms',
        loadComponent: () => import('./core/pages/legal/terms.component').then(m => m.TermsComponent),
        title: 'Algemene Voorwaarden - ARES',
    },
    {
        path: 'cookies',
        loadComponent: () => import('./core/pages/legal/cookie-policy.component').then(m => m.CookiePolicyComponent),
        title: 'Cookie Policy - ARES',
    },
    {
        path: 'about',
        loadComponent: () => import('./core/pages/about/about.component').then(m => m.AboutComponent),
        title: 'Over ons - ARES',
    },
    {
        path: 'contact',
        loadComponent: () => import('./core/pages/contact/contact.component').then(m => m.ContactComponent),
        title: 'Contact - ARES',
    },
    // Superadmin page - user management and migration
    {
        path: 'superadmin',
        loadComponent: () => import('./core/pages/superadmin/superadmin-page.component').then(m => m.SuperadminPageComponent),
        title: 'Superadmin - ARES',
        canActivate: [superadminGuard],
    },
    // Login page
    {
        path: 'login',
        title: 'Inloggen - ARES Salesplatform',
        loadComponent: () => import('./core/pages/auth/login/login.component').then(m => m.LoginComponent),
    },
    // Auth routes
    {
        path: 'auth',
        children: [
            {
                path: 'login',
                title: 'Inloggen - ARES Salesplatform',
                loadComponent: () => import('./core/pages/auth/login/login.component').then(m => m.LoginComponent),
            },
            {
                path: 'callback',
                title: 'Inloggen...',
                loadComponent: () => import('./core/pages/auth/callback/auth-callback.component').then(m => m.AuthCallbackComponent),
            },
            {
                path: 'accept-invitation',
                title: 'Uitnodiging accepteren - ARES Salesplatform',
                loadComponent: () => import('./core/pages/auth/accept-invitation/accept-invitation.component').then(m => m.AcceptInvitationComponent),
            },
            {
                path: 'forgot-password',
                title: 'Wachtwoord vergeten - ARES Salesplatform',
                loadComponent: () => import('./core/pages/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
            },
            {
                path: 'reset-password',
                title: 'Wachtwoord opnieuw instellen - ARES Salesplatform',
                loadComponent: () => import('./core/pages/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
            },
            {
                path: 'set-password',
                title: 'Wachtwoord instellen - ARES Salesplatform',
                loadComponent: () => import('./core/pages/auth/set-password/set-password.component').then(m => m.SetPasswordComponent),
            },
        ],
    },
    // Agent portal routes (/my/*)
    {
        path: 'my',
        canActivate: [authWithPortalRedirectGuard, organizationInitializedGuard, agentGuard],
        loadChildren: () => import('./modules/agent-portal/agent-portal.module').then(m => m.AgentPortalModule),
    },
    // App routes (subdomain only - demo.ares.sale)
    {
        path: '',
        canActivate: [
            authWithPortalRedirectGuard,
            organizationInitializedGuard,
            managementGuard,
        ],
        title: 'ARES Salesplatform',
        children: [
            {
                path: '',
                component: HomePageComponent,
                canActivate: [
                    redirectAwayGuard,
                ],
            },
            {
                path: 'customers',
                title: 'Klanten - ARES Salesplatform',
                loadChildren: () => import('./modules/customers/customers.module').then(m => m.CustomersModule),
            },
            {
                path: 'meldingen',
                title: 'Meldingen - ARES Salesplatform',
                loadComponent: () =>
                    import('./core/pages/notifications/notifications-page.component').then(
                        m => m.NotificationsPageComponent,
                    ),
            },
            {
                path: 'establishment',
                title: 'Vestigingen - ARES Salesplatform',
                component: EstablishmentsComponent,
                resolve: {
                    breadcrumb: establishmentBreadcrumbResolver,
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'dashboard',
                        pathMatch: 'full',
                    },
                    {
                        path: 'dashboard',
                        title: 'Dashboard - ARES Salesplatform',
                        loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
                    },
                    {
                        path: 'planning',
                        title: 'Planning - ARES Salesplatform',
                        loadChildren: () => import('./modules/planning/planning.module').then(m => m.PlanningModule),
                    },
                    {
                        path: 'employees',
                        title: 'Medewerkers - ARES Salesplatform',
                        loadChildren: () => import('./modules/employees/employees.module').then(m => m.EmployeesModule),
                    },
                    {
                        path: 'sales',
                        title: 'Sales - ARES Salesplatform',
                        loadChildren: () => import('./modules/sales/sales.module').then(m => m.SalesModule),
                    },
                    {
                        path: 'fees',
                        title: 'Uitbetalingen - ARES Salesplatform',
                        loadChildren: () => import('./modules/payroll/payroll.module').then(m => m.PayrollModule),
                    },
                    {
                        path: 'financieel',
                        title: 'Financieel - ARES Salesplatform',
                        loadChildren: () => import('./modules/financieel/financieel.module').then(m => m.FinancieelModule),
                    },
                ],
            },
            {
                path: 'profile',
                title: 'Profiel - ARES Salesplatform',
                component: ProfilePageComponent,
            },
            {
                path: 'settings',
                title: 'Instellingen - ARES Salesplatform',
                loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
            },
            {
                path: 'afboekingen',
                title: 'Afboekingen - ARES Salesplatform',
                loadChildren: () => import('./modules/salesdock/salesdock.module').then(m => m.SalesdockModule),
            },
        ],
    },
    {
        path: 'organization-not-found',
        title: 'Organisatie niet gevonden - ARES Salesplatform',
        component: OrganizationNotFoundComponent,
        canActivate: [
            organizationNotInitializedGuard,
        ],
    },
    // Default route
    {
        path: '**',
        title: 'Pagina niet gevonden - ARES Salesplatform',
        component: NotFoundComponent,
    },
];
